/**
 * @fileoverview This file contains Zod schemas for validating project-related
 * data structures, ensuring consistency between client-side forms and server-side
 * operations.
 */

import { z } from 'zod';
import { MAX_TITLE_LENGTH, MIN_BLURB_LENGTH, MAX_BLURB_LENGTH, MIN_TITLE_LENGTH } from '../constants';

/**
 * Schema for validating the input for creating a new project.
 * Primarily focuses on the initial blurb.
 */
export const NewProjectFormSchema = z.object({
  title: z.string()
    .min(MIN_TITLE_LENGTH, `Title must be at least ${MIN_TITLE_LENGTH} characters.`)
    .max(MAX_TITLE_LENGTH, `Title must not be longer than ${MAX_TITLE_LENGTH} characters.`)
    .describe("The title of the new story project."),
  blurb: z.string()
    .min(MIN_BLURB_LENGTH, `Blurb must be at least ${MIN_BLURB_LENGTH} characters.`)
    .max(MAX_BLURB_LENGTH, `Blurb must not be longer than ${MAX_BLURB_LENGTH} characters.`)
    .describe("The initial story blurb or concept that forms the foundation of the project."),
}).describe("Form input schema for creating a new story project with a title and blurb.");

/**
 * Schema for validating the options when duplicating a project.
 * Includes a refinement to ensure `chapterContent` can only be true if
 * `chapterOutlines` is also true, enforcing logical consistency.
 */
export const DuplicateProjectFormSchema = z.object({
  title: z.string()
    .min(MIN_TITLE_LENGTH, `Title must be at least ${MIN_TITLE_LENGTH} characters.`)
    .max(MAX_TITLE_LENGTH)
    .describe("The title for the duplicated project."),
  options: z.object({
    storySetup: z.boolean().default(true).describe("Whether to include the story setup in the duplicate."),
    plot: z.boolean().default(true).describe("Whether to include plot elements in the duplicate."),
    characters: z.boolean().default(true).describe("Whether to include characters in the duplicate."),
    worldElements: z.boolean().default(true).describe("Whether to include world building elements in the duplicate."),
    chapterOutlines: z.boolean().default(true).describe("Whether to include chapter outlines in the duplicate."),
    chapterContent: z.boolean().default(false).describe("Whether to include written chapter content in the duplicate."),
    styleGuides: z.boolean().default(true).describe("Whether to include style guides in the duplicate."),
  }).describe("Duplication options specifying which elements to copy from the original project."),
}).describe("Form input schema for duplicating an existing project with selective copying options.").refine(data => {
    // If chapterContent is checked, chapterOutlines must also be checked.
    if (data.options.chapterContent) {
        return data.options.chapterOutlines;
    }
    return true;
}, {
    // This message isn't strictly needed as the UI disables the checkbox,
    // but it's good practice for backend validation.
    message: "Cannot copy chapter content without also copying the chapter outlines.",
    path: ["options.chapterContent"],
});

/**
 * Type for the duplication options, inferred from the Zod schema to ensure consistency.
 */
export type DuplicationOptions = z.infer<typeof DuplicateProjectFormSchema>['options'];
